import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  IconButton,
  Button,
  LinearProgress,
  Box,
  Chip,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip
} from '@mui/material';
import {
  PictureAsPdf,
  Description,
  Favorite,
  FavoriteBorder,
  MoreVert,
  PlayArrow,
  Delete,
  Edit,
  Download
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';

const DocumentCard = ({ 
  document, 
  progress, 
  onOpen, 
  onDelete, 
  onRename, 
  onToggleFavorite,
  onDownload,
  viewMode = 'grid'
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [newName, setNewName] = useState(document.original_filename);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleRename = () => {
    onRename(document.id, newName);
    setRenameDialogOpen(false);
    handleMenuClose();
  };

  const handleDelete = () => {
    onDelete(document.id);
    handleMenuClose();
  };

  const handleToggleFavorite = () => {
    onToggleFavorite(document.id);
    handleMenuClose();
  };

  const handleDownload = () => {
    onDownload(document);
    handleMenuClose();
  };

  const getFileIcon = () => {
    if (document.file_type === 'application/pdf') {
      return <PictureAsPdf sx={{ fontSize: 40, color: '#d32f2f' }} />;
    }
    return <Description sx={{ fontSize: 40, color: '#1976d2' }} />;
  };

  const getFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getProgressPercentage = () => {
    return progress ? progress.progress_percentage || 0 : 0;
  };

  const getProgressText = () => {
    if (!progress) return 'Not started';
    
    if (document.file_type === 'application/pdf') {
      return `Page ${progress.current_page || 1} of ${progress.total_pages || 1}`;
    } else {
      return `${Math.round(progress.progress_percentage || 0)}% read`;
    }
  };

  const getLastOpenedText = () => {
    try {
      return formatDistanceToNow(new Date(document.last_opened), { addSuffix: true });
    } catch (error) {
      return 'Unknown';
    }
  };

  const progressPercentage = getProgressPercentage();

  if (viewMode === 'list') {
    return (
      <>
        <Card
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
          }}
        >
          {document.is_favorite && (
            <Chip
              icon={<Favorite />}
              label="Favorite"
              size="small"
              color="error"
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                zIndex: 1,
              }}
            />
          )}

          <CardContent sx={{ py: 1.25, '&:last-child': { pb: 1.25 } }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                minHeight: 56,
              }}
            >
              <Box sx={{ flexShrink: 0 }}>
                {document.file_type === 'application/pdf'
                  ? <PictureAsPdf sx={{ fontSize: 32, color: '#d32f2f' }} />
                  : <Description sx={{ fontSize: 32, color: '#1976d2' }} />}
              </Box>

              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Tooltip title={document.original_filename}>
                  <Typography
                    variant="subtitle1"
                    component="h3"
                    sx={{
                      fontWeight: 600,
                      lineHeight: 1.2,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {document.original_filename}
                  </Typography>
                </Tooltip>

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.25 }}>
                  <Typography variant="caption" color="text.secondary">
                    {getFileSize(document.file_size)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    • {getProgressText()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    • {getLastOpenedText()}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                <Button
                  variant="contained"
                  startIcon={<PlayArrow />}
                  onClick={() => onOpen(document)}
                  size="small"
                  sx={{ minHeight: 40, px: 2 }}
                >
                  {progress && progressPercentage > 0 ? 'Continue' : 'Open'}
                </Button>

                <IconButton
                  onClick={handleToggleFavorite}
                  size="small"
                  color={document.is_favorite ? 'error' : 'default'}
                  sx={{ width: 40, height: 40 }}
                >
                  {document.is_favorite ? <Favorite /> : <FavoriteBorder />}
                </IconButton>

                <IconButton onClick={handleMenuOpen} size="small" sx={{ width: 40, height: 40 }}>
                  <MoreVert />
                </IconButton>
              </Box>
            </Box>

            <Box sx={{ mt: 1 }}>
              <LinearProgress
                variant="determinate"
                value={progressPercentage}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: 'rgba(0,0,0,0.08)',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 3,
                  },
                }}
              />
            </Box>
          </CardContent>
        </Card>

        {/* Context Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={() => setRenameDialogOpen(true)}>
            <Edit sx={{ mr: 1 }} />
            Rename
          </MenuItem>
          <MenuItem onClick={handleDownload}>
            <Download sx={{ mr: 1 }} />
            Download
          </MenuItem>
          <MenuItem onClick={handleToggleFavorite}>
            {document.is_favorite ? <FavoriteBorder sx={{ mr: 1 }} /> : <Favorite sx={{ mr: 1 }} />}
            {document.is_favorite ? 'Remove from Favorites' : 'Add to Favorites'}
          </MenuItem>
          <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
            <Delete sx={{ mr: 1 }} />
            Delete
          </MenuItem>
        </Menu>

        {/* Rename Dialog */}
        <Dialog open={renameDialogOpen} onClose={() => setRenameDialogOpen(false)}>
          <DialogTitle>Rename Document</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Document Name"
              fullWidth
              variant="outlined"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRenameDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleRename} variant="contained">Rename</Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  return (
    <>
      <Card 
        sx={{ 
          height: '100%', 
          display: 'flex', 
          flexDirection: 'column',
          position: 'relative',
          '&:hover': {
            boxShadow: 4,
            transform: 'translateY(-2px)',
            transition: 'all 0.2s ease-in-out'
          }
        }}
      >
        {/* Favorite indicator */}
        {document.is_favorite && (
          <Chip
            icon={<Favorite />}
            label="Favorite"
            size="small"
            color="error"
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 1
            }}
          />
        )}

        <CardContent sx={{ flexGrow: 1, pb: 1 }}>
          {/* File icon and basic info */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
            <Box sx={{ mr: 2, flexShrink: 0 }}>
              {getFileIcon()}
            </Box>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Tooltip title={document.original_filename}>
                <Typography 
                  variant="h6" 
                  component="h3"
                  sx={{ 
                    fontSize: '1rem',
                    fontWeight: 600,
                    mb: 0.5,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {document.original_filename}
                </Typography>
              </Tooltip>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                {getFileSize(document.file_size)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Added {getLastOpenedText()}
              </Typography>
            </Box>
          </Box>

          {/* Progress section */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Progress
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {getProgressText()}
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={progressPercentage} 
              sx={{ 
                height: 6, 
                borderRadius: 3,
                backgroundColor: 'rgba(0,0,0,0.1)',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 3
                }
              }}
            />
          </Box>

          {/* Reading time */}
          {progress && progress.reading_time_minutes > 0 && (
            <Typography variant="caption" color="text.secondary">
              Reading time: {progress.reading_time_minutes} min
            </Typography>
          )}
        </CardContent>

        <CardActions sx={{ pt: 0, justifyContent: 'space-between' }}>
          <Button
            variant="contained"
            startIcon={<PlayArrow />}
            onClick={() => onOpen(document)}
            size="small"
            sx={{ flexGrow: 1, mr: 1 }}
          >
            {progress && progressPercentage > 0 ? 'Continue' : 'Open'}
          </Button>
          
          <IconButton
            onClick={handleToggleFavorite}
            size="small"
            color={document.is_favorite ? 'error' : 'default'}
          >
            {document.is_favorite ? <Favorite /> : <FavoriteBorder />}
          </IconButton>

          <IconButton onClick={handleMenuOpen} size="small">
            <MoreVert />
          </IconButton>
        </CardActions>
      </Card>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => setRenameDialogOpen(true)}>
          <Edit sx={{ mr: 1 }} />
          Rename
        </MenuItem>
        <MenuItem onClick={handleDownload}>
          <Download sx={{ mr: 1 }} />
          Download
        </MenuItem>
        <MenuItem onClick={handleToggleFavorite}>
          {document.is_favorite ? <FavoriteBorder sx={{ mr: 1 }} /> : <Favorite sx={{ mr: 1 }} />}
          {document.is_favorite ? 'Remove from Favorites' : 'Add to Favorites'}
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <Delete sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Rename Dialog */}
      <Dialog open={renameDialogOpen} onClose={() => setRenameDialogOpen(false)}>
        <DialogTitle>Rename Document</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Document Name"
            fullWidth
            variant="outlined"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleRename} variant="contained">Rename</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DocumentCard;
